import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { ApplicationViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';


@Component({
    selector: 'app-application-add-edit',
    templateUrl: './application-add-edit.component.html',
    styleUrls: ['./application-add-edit.component.scss'],
    standalone: false
})
export class ApplicationAddEditComponent implements OnInit {
  inputFormGroup: UntypedFormGroup;
  applicationToUpdate: ApplicationViewModel | null = null;

  title: string = '';
  genericMissingValueMessage: string = 'This is a required field';

  isEditMode: boolean = false;
  isCreateMode: boolean = false;
  submitClicked: boolean = false;
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<ApplicationAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) private readonly application: ApplicationViewModel | null
  ) {
    this.inputFormGroup = this.formBuilder.group({
      name: ['', Validators.required],
      shortName: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.applicationToUpdate = this.application ? new ApplicationViewModel(this.application) : null;
    this.isEditMode = this.applicationToUpdate !== null;
    this.isCreateMode = !this.isEditMode;

    if (this.isCreateMode) {
      this.title = 'Create a new application';
    }

    if (this.applicationToUpdate) {
      this.title = `Edit ${this.applicationToUpdate.name}`;
      this.setFormValues();
    }
  }

  setFormValues(): void {
    if (!this.applicationToUpdate) {
      return;
    }

    this.inputFormGroup.setValue({
      name:      this.applicationToUpdate.name      || '',
      shortName: this.applicationToUpdate.shortName || ''
    });
  }

  save(): void {
    this.submitClicked = true;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
      return;
    }

    const payload: ApplicationViewModel = new ApplicationViewModel({
      name: this.inputFormGroup.controls['name'].value,
      shortName: this.inputFormGroup.controls['shortName'].value,
      isActive: true
    });

    if (this.isCreateMode) {
      this.esraApiClient.createApplication(payload).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, `The new application, ${payload.name}, was successfully created.`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackBarService.showSnackBar(true, err, 'mat-snack-bar-error');
        }
      });
    }

    const applicationToUpdate: ApplicationViewModel | null = this.applicationToUpdate;
    if (this.isEditMode && applicationToUpdate) {
      applicationToUpdate.name = payload.name;
      applicationToUpdate.shortName = payload.shortName;
      this.esraApiClient.updateApplication(applicationToUpdate).pipe(
        indicate(this.saving$),
        first()
      ).subscribe({
        next: () => {
          this.snackBarService.showSnackBar(true, `Application was updated to ${applicationToUpdate.name}`);
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.snackBarService.showSnackBar(true, err, 'mat-snack-bar-error');
        }
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
